import React, { useState } from 'react';
import axios from 'axios';
import './BOMUpdateModal.css';

/**
 * BOM Update Modal
 * Shows when work order components differ from current assembly item BOM
 * Allows user to update the work order with current BOM
 */
function BOMUpdateModal({ bomData, onClose, onUpdate }) {
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);

  if (!bomData || !bomData.hasChanges) return null;

  const handleUpdateBOM = async () => {
    setUpdating(true);
    setError(null);

    try {
      // Format BOM components for the update API
      const bomComponents = bomData.bomComponents.map(comp => ({
        itemId: comp.itemId,
        sku: comp.sku,
        description: comp.description,
        quantity: comp.quantity || 1
      }));

      const response = await axios.post(`/api/workorders/${bomData.workOrderId}/update-bom`, {
        bomComponents
      });

      if (response.data.success) {
        onUpdate && onUpdate(response.data);
        onClose();
      } else {
        setError(response.data.message || 'Failed to update work order');
      }
    } catch (err) {
      console.error('Error updating BOM:', err);
      setError(err.response?.data?.error || err.message || 'Failed to update work order');
    } finally {
      setUpdating(false);
    }
  };

  const getChangeIcon = (type) => {
    switch (type) {
      case 'wick': return '🕯️';
      case 'wax': return '🧴';
      case 'added': return '➕';
      case 'removed': return '➖';
      default: return '🔄';
    }
  };

  const getChangeLabel = (type) => {
    switch (type) {
      case 'wick': return 'Wick Changed';
      case 'wax': return 'Wax Changed';
      case 'added': return 'New Component';
      case 'removed': return 'Removed Component';
      default: return 'Changed';
    }
  };

  return (
    <div className="bom-update-modal-overlay">
      <div className="bom-update-modal">
        <h3>🔄 BOM Update Available</h3>
        <p>
          The Bill of Materials for <strong>{bomData.assemblyItemSku}</strong> has been updated.
          Would you like to update work order <strong>{bomData.workOrderNumber}</strong>?
        </p>

        <div className="bom-changes-list">
          <h4>Changes:</h4>
          <table className="bom-changes-table">
            <thead>
              <tr>
                <th>Component</th>
                <th>Current (WO)</th>
                <th></th>
                <th>Updated (BOM)</th>
              </tr>
            </thead>
            <tbody>
              {bomData.changes.map((change, index) => (
                <tr key={index} className={`change-row change-${change.type}`}>
                  <td className="change-component">
                    <span className="change-icon">{getChangeIcon(change.type)}</span>
                    <span className="change-label">{getChangeLabel(change.type)}</span>
                  </td>
                  <td className="change-old">{change.woValue}</td>
                  <td className="change-arrow">→</td>
                  <td className="change-new">{change.bomValue}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {error && (
          <div className="bom-error-message">
            <p>Error: {error}</p>
          </div>
        )}

        <div className="bom-modal-message">
          <p>Updating will replace the work order components with the current BOM in NetSuite.</p>
        </div>

        <div className="bom-modal-actions">
          <button
            className="bom-cancel-btn"
            onClick={onClose}
            disabled={updating}
          >
            Keep Current
          </button>
          <button
            className="bom-update-btn"
            onClick={handleUpdateBOM}
            disabled={updating}
          >
            {updating ? 'Updating...' : 'Update Work Order'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default BOMUpdateModal;
